import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories, videos } from "@/mocks/videos";

const siteUrl = "https://www.jiuseshipin.com";

const schemaData = (video: typeof videos[0], catName: string) => [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": video.title,
    "description": video.description,
    "url": `${siteUrl}/article/${video.id}`,
    "datePublished": "2026-08-01T08:00:00+08:00",
    "dateModified": "2026-08-07T10:00:00+08:00",
    "author": { "@type": "Organization", "name": "九色视频" },
    "publisher": { "@type": "Organization", "name": "九色视频", "url": siteUrl },
    "image": video.thumbnail,
    "about": { "@type": "Thing", "name": catName },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": siteUrl },
      { "@type": "ListItem", "position": 2, "name": catName, "item": `${siteUrl}/category/${video.category}` },
      { "@type": "ListItem", "position": 3, "name": video.title, "item": `${siteUrl}/article/${video.id}` },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": video.title,
    "description": video.description,
    "thumbnailUrl": video.thumbnail,
    "uploadDate": "2026-08-01T08:00:00+08:00",
    "duration": video.duration,
    "contentUrl": `${siteUrl}/article/${video.id}`,
  },
];

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <>
        <Navbar />
        <div className="pt-24 text-center py-20">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">视频未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.id === video.category);
  const relatedVideos = videos.filter((v) => v.category === video.category && v.id !== video.id).slice(0, 4);

  return (
    <>
      <SEO
        title={`${video.title} - 九色视频在线观看`}
        description={`${video.description.substring(0, 140)} - 九色视频高清在线观看。`}
        keywords={`九色视频,${video.title},${cat?.name || ""}在线观看,高清视频播放`}
        canonicalUrl={`${siteUrl}/article/${video.id}`}
        ogType="article"
        ogImage={video.thumbnail}
        schemaData={schemaData(video, cat?.name || "")}
      />

      <Navbar />

      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: cat?.name || "分类", href: `/category/${video.category}` },
              { label: video.title },
            ]}
          />

          {/* Video Player Area */}
          <div className="relative aspect-video rounded-2xl overflow-hidden mb-8 bg-foreground-950">
            <img
              src={video.thumbnail}
              alt={video.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer group">
              <div className="w-20 h-20 rounded-full bg-primary-500/90 flex items-center justify-center group-hover:bg-primary-500 transition-colors group-hover:scale-110 transition-transform duration-300">
                <i className="ri-play-fill text-background-50 text-4xl ml-1"></i>
              </div>
            </div>
          </div>

          {/* Article Header */}
          <header className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-snug">
              {video.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              {cat && (
                <Link
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1 rounded-md text-xs font-medium ${cat.color} transition-colors hover:opacity-80`}
                >
                  {cat.name}
                </Link>
              )}
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {video.duration}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {video.views} 次观看
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                {video.rating} 分
              </span>
            </div>
          </header>

          {/* Article Content */}
          <section className="mb-12">
            <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">视频简介</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-foreground-700 leading-relaxed text-base">{video.description}</p>
              <p className="text-foreground-600 leading-relaxed text-base mt-4">
                九色视频为您提供{video.title}的高清完整版在线观看服务。我们采用先进的视频编码技术和全球CDN加速网络，
                确保您在各类设备和网络环境下都能获得流畅清晰的播放体验。本视频支持多档画质选择，从标清到4K超高清，
                满足不同场景下的观看需求。
              </p>
              <p className="text-foreground-600 leading-relaxed text-base mt-4">
                九色视频平台汇聚海量高清视频资源，涵盖电影、电视剧、综艺、动漫、纪录片、音乐、游戏、体育、教育
                等九大内容分类。我们的编辑团队每日精心筛选优质内容，智能推荐算法根据您的观影偏好精准推送，
                助您发现更多精彩。立即访问九色视频，开启您的视频探索之旅。
              </p>
            </div>
          </section>

          {/* User Interactions */}
          <div className="flex items-center gap-3 mb-12 py-4 border-y border-background-200">
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
              <i className="ri-thumb-up-line text-lg"></i>
              <span>点赞 (3.2万)</span>
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
              <i className="ri-star-line text-lg"></i>
              <span>收藏</span>
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
              <i className="ri-share-forward-line text-lg"></i>
              <span>分享</span>
            </button>
          </div>

          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <section>
              <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-product-shop>
                {relatedVideos.map((rv) => {
                  const rvCat = categories.find((c) => c.id === rv.category);
                  return (
                    <Link
                      key={rv.id}
                      to={`/article/${rv.id}`}
                      className="group flex gap-4 bg-background-100 rounded-2xl p-4 transition-all duration-300 hover:bg-background-200/70 cursor-pointer"
                    >
                      <div className="relative w-40 h-24 flex-shrink-0 rounded-xl overflow-hidden">
                        <img
                          src={rv.thumbnail}
                          alt={rv.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 bg-black/70 text-white text-[10px] rounded font-medium">
                          {rv.duration}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-heading font-semibold text-foreground-950 text-sm mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rv.title}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                          {rvCat && (
                            <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${rvCat.color}`}>
                              {rvCat.name}
                            </span>
                          )}
                          <span><i className="ri-eye-line mr-1"></i>{rv.views}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}